import type { Timestamp } from 'firebase/firestore';

/**
 * Calculates the usage period key (YYYY-MM-DD) based on the subscription end date.
 * Handles both monthly and multi-month (e.g. annual) subscriptions by calculating the
 * next monthly billing anchor relative to the current date.
 *
 * This mirrors the logic in functions/utils/usage.ts.
 *
 * @param subscriptionEnd The end date of the subscription period.
 * @returns A string key in YYYY-MM-DD format representing the end of the current usage month.
 */
export const getUsagePeriodKey = (
  subscriptionEnd: Date | string | number | Timestamp | null | undefined
): string => {
  const now = new Date();

  if (!subscriptionEnd) {
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  let endDate: Date;
  // Handle Firestore Timestamp
  if (
    typeof subscriptionEnd === 'object' &&
    'toDate' in subscriptionEnd &&
    typeof subscriptionEnd.toDate === 'function'
  ) {
    endDate = subscriptionEnd.toDate();
  } else {
    endDate = new Date(subscriptionEnd as string | number | Date);
  }

  if (Number.isNaN(endDate.getTime())) {
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  // If subscription has ended or ends in the past relative to now, just use the end date
  if (endDate < now) {
    return endDate.toISOString().split('T')[0] || '';
  }

  const anchorDay = endDate.getUTCDate();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();

  // Helper to construct a date ensuring day clamping (e.g. Feb 31 -> Feb 28)
  const getDateWithDayClamp = (year: number, month: number, day: number) => {
    // Use Date.UTC to avoid local timezone shifts
    const date = new Date(Date.UTC(year, month + 1, 0)); // Last day of the month in UTC
    const lastDayOfMonth = date.getUTCDate();
    return new Date(Date.UTC(year, month, Math.min(day, lastDayOfMonth)));
  };

  // 1. Try the billing date in the current month
  let candidate = getDateWithDayClamp(currentYear, currentMonth, anchorDay);

  // 2. If today is AFTER this candidate date, move to next month.
  // Normalize 'now' to start of day for stable day comparison in UTC
  const nowStartOfDay = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );

  if (nowStartOfDay > candidate) {
    candidate = getDateWithDayClamp(currentYear, currentMonth + 1, anchorDay);
  }

  // 3. Cap at subscriptionEnd (for the final partial month of an annual sub)
  if (candidate > endDate) {
    return endDate.toISOString().split('T')[0] || '';
  }

  return candidate.toISOString().split('T')[0] || '';
};

/**
 * Fills in any missing billing cycles with zero-usage records between the oldest
 * recorded usage and the current billing cycle.
 */
export const fillMissingBillingCycles = (
  monthly: Record<string, Record<string, number>>,
  subscriptionEnd: Date | string | number | Timestamp | null | undefined
): Record<string, Record<string, number>> => {
  const currentKey = getUsagePeriodKey(subscriptionEnd);

  if (!monthly[currentKey]) {
    monthly[currentKey] = {
      tenders: 0,
      reports: 0,
      drafts: 0,
      reviews: 0,
      adhoc_documents: 0,
      section_chats: 0,
    };
  }

  const keys = Object.keys(monthly).sort();
  if (keys.length <= 1) {
    return monthly;
  }

  const oldestKey = keys[0];

  let anchorDay = 1;
  if (subscriptionEnd) {
    let endDate: Date;
    if (
      typeof subscriptionEnd === 'object' &&
      'toDate' in subscriptionEnd &&
      typeof subscriptionEnd.toDate === 'function'
    ) {
      endDate = subscriptionEnd.toDate();
    } else {
      endDate = new Date(subscriptionEnd as string | number | Date);
    }
    if (!Number.isNaN(endDate.getTime())) {
      anchorDay = endDate.getUTCDate();
    }
  }

  const oldestDate = new Date(oldestKey as string);
  let currentYear = oldestDate.getUTCFullYear();
  let currentMonth = oldestDate.getUTCMonth();

  const getDateWithDayClamp = (year: number, month: number, day: number) => {
    const date = new Date(Date.UTC(year, month + 1, 0));
    const lastDayOfMonth = date.getUTCDate();
    return new Date(Date.UTC(year, month, Math.min(day, lastDayOfMonth)));
  };

  // Prevent infinite loops just in case
  let iterations = 0;
  const MAX_ITERATIONS = 120; // 10 years max backfill

  while (iterations < MAX_ITERATIONS) {
    currentMonth++;
    if (currentMonth > 11) {
      currentMonth = 0;
      currentYear++;
    }

    const candidateDate = getDateWithDayClamp(currentYear, currentMonth, anchorDay);
    const candidateKeyStr = candidateDate.toISOString().split('T')[0];
    if (!candidateKeyStr) {
      break;
    }
    const candidateKey = candidateKeyStr;

    if (candidateKey >= currentKey) {
      break;
    }

    if (!monthly[candidateKey]) {
      monthly[candidateKey] = {
        tenders: 0,
        reports: 0,
        drafts: 0,
        reviews: 0,
        adhoc_documents: 0,
        section_chats: 0,
      };
    }
    iterations++;
  }

  return monthly;
};
